import { Icon } from '@iconify/react';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { canAccess, MODULES } from 'auth/permissions';
import { useAuth } from 'modules/auth/auth.context';
import { ConfirmationModal, SectionTitle } from 'modules/shared/components';
import { Table } from 'modules/shared/components/data-display';
import useAsyncData from 'modules/shared/hooks/useAsyncData';
import { formatDate } from 'modules/shared/utils';
import { useCallback, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { logError } from 'utils/logger';
import IssueLetterModal from '../components/IssueLetterModal';
import LetterViewModal from '../components/LetterViewModal';
import { deleteLetter, getLetters, LetterRow } from '../people.api';

/**
 * The register of letters and certificates HR has issued.
 *
 * The screen used to be a "coming soon" page over a list of eight document names with inert
 * Add and Compose buttons — no templates, no storage, no record of anything ever issued.
 */
export default function LettersCertificatesPage() {
  const { role } = useAuth();
  const canIssue = canAccess(role, MODULES.LETTERS_ISSUE);

  const [search, setSearch] = useState('');
  const [issuing, setIssuing] = useState(false);
  const [viewing, setViewing] = useState<LetterRow | null>(null);
  const [deleting, setDeleting] = useState<LetterRow | null>(null);
  const [busy, setBusy] = useState(false);

  const fetcher = useCallback(() => getLetters(), []);
  const { data, loading, error, refetch } = useAsyncData<LetterRow[]>(fetcher, [], {
    code: 'LETTERS_FETCH_FAILED',
    errorMessage: 'Failed to load letters',
  });

  const visible = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return data;
    return data.filter((row) =>
      [row.employee_name, row.employee_code, row.letter_label, row.reference_no, row.subject]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(term))
    );
  }, [data, search]);

  const handleDelete = async () => {
    if (!deleting) return;
    setBusy(true);
    try {
      await deleteLetter(deleting.id);
      toast.success(`${deleting.reference_no} deleted`);
      setDeleting(null);
      await refetch();
    } catch (err) {
      toast.error(
        logError({ error: err, message: 'Failed to delete the letter', code: 'LETTER_DELETE_FAILED' })
      );
    } finally {
      setBusy(false);
    }
  };

  const columns = useMemo(
    () => [
      { id: 'reference_no', dataKey: 'reference_no', label: 'Reference', align: 'left' as const, sortable: true },
      {
        id: 'issued_on',
        dataKey: 'issued_on',
        label: 'Issued',
        align: 'center' as const,
        sortable: true,
        renderCell: (value: unknown) => (value ? formatDate(value as string) : '—'),
      },
      { id: 'employee_name', dataKey: 'employee_name', label: 'Employee', align: 'left' as const, sortable: true },
      { id: 'department', dataKey: 'department', label: 'Department', align: 'left' as const },
      {
        id: 'letter_label',
        dataKey: 'letter_label',
        label: 'Type',
        align: 'left' as const,
        sortable: true,
        renderCell: (value: unknown) => <Chip size='small' label={String(value)} variant='outlined' />,
      },
      { id: 'subject', dataKey: 'subject', label: 'Subject', align: 'left' as const },
      { id: 'issued_by', dataKey: 'issued_by', label: 'Issued by', align: 'left' as const },
      {
        id: 'actions',
        dataKey: 'id',
        label: '',
        align: 'center' as const,
        renderCell: (_value: unknown, row: LetterRow) => (
          <Stack direction='row' justifyContent='center'>
            <Tooltip title='View / print'>
              <IconButton size='small' onClick={() => setViewing(row)}>
                <Icon icon='mdi:file-document-outline' width={18} />
              </IconButton>
            </Tooltip>
            {canIssue && (
              <Tooltip title='Delete'>
                <IconButton size='small' onClick={() => setDeleting(row)}>
                  <Icon icon='mdi:trash-can-outline' width={18} />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        ),
      },
    ],
    [canIssue]
  );

  return (
    <Stack gap={3}>
      <SectionTitle
        title='LETTERS & CERTIFICATES'
        action={
          <Stack direction='row' gap={2} alignItems='center'>
            <TextField
              size='small'
              placeholder='Search employee, type or reference'
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              sx={{ minWidth: 280 }}
            />
            {canIssue && (
              <Button
                variant='contained'
                startIcon={<Icon icon='mdi:plus' />}
                onClick={() => setIssuing(true)}
              >
                Issue letter
              </Button>
            )}
          </Stack>
        }
      />

      {error && (
        <Alert severity='error' action={<Button onClick={refetch}>Retry</Button>}>
          {error}
        </Alert>
      )}

      <Typography variant='body2' color='text.secondary'>
        Every letter is stored as it was issued, with its own reference number, so a certificate
        keeps saying what it said after a promotion or a salary revision.
      </Typography>

      <Table
        columns={columns}
        data={visible}
        loading={loading}
        emptyMessage={search ? 'No letters match that search' : 'No letters issued yet'}
      />

      <IssueLetterModal
        open={issuing}
        onClose={() => setIssuing(false)}
        onIssued={async (letter) => {
          await refetch();
          setViewing(letter);
        }}
      />

      <LetterViewModal letter={viewing} onClose={() => setViewing(null)} />

      <ConfirmationModal
        open={Boolean(deleting)}
        onClose={() => setDeleting(null)}
        loading={busy}
        icon='mdi:trash-can-outline'
        title={`Delete ${deleting?.reference_no ?? 'letter'}?`}
        message='The issued letter is removed from the register. This cannot be undone.'
        confirmText='Delete'
        onConfirm={handleDelete}
      />
    </Stack>
  );
}
